/**
 * Administração → Tarifas do contrato (`SC-12`, ADR-084).
 *
 * O que cada concessão cobra, em duas partes que a tela não mistura:
 *
 * - **a estrutura do contrato** — as categorias com os eixos e a rodagem, os multiplicadores,
 *   o desconto de cada meio de pagamento, a especial, a isenta, o arredondamento, o reajuste,
 *   o usuário frequente e os pórticos com a tarifa da categoria 1 que a conta dá ao lado da que
 *   o contrato escreve. É do domínio (`transit_domain.estrutura_tarifaria`), apontada pelo perfil
 *   do poder concedente da concessão; sem perfil, a padrão do cockpit, de 1 a 8. Ninguém a edita
 *   aqui: contrato novo é estrutura nova, com teste.
 * - **o cadastro** — o valor de cada categoria, com vigência e fonte (`R-12`), que é o que a
 *   passagem paga hoje. A tela mostra o histórico e avisa a categoria cadastrada que a estrutura
 *   do contrato não tem.
 *
 * A categoria 8 do padrão é a moto; a de MS é o caminhão de 6 eixos — é por isso que a
 * estrutura vem antes do cadastro.
 *
 * **A tabela nova** (`OB-11`, ADR-086): a supervisão e o administrador cadastram a tabela da
 * concessão por planilha — o modelo vem daqui, com a vigente ou a conta do contrato —, com
 * prévia antes de gravar. A conferência é do servidor (`transit_domain.tarifa.conferir_tabela`):
 * a tela mostra os problemas, que impedem, e os avisos, que pedem conferência. A tabela entra
 * inteira, a partir da meia-noite de um dia que ainda não chegou, e encerra a vigente.
 */
import { useCallback, useEffect, useState } from "react";

import { ErroDaApi, api } from "../api/cliente";
import type {
  ApontamentoNaTela,
  CategoriaDaEstrutura,
  EstruturaNaTela,
  ResultadoDaTabela,
  TarifaCadastrada,
  TarifasDaConcessao,
  TarifasNaTela,
  TrechoNaTela,
} from "../api/tipos";
import { Badge, Botao, Estado, Painel } from "../componentes/base";
import { Icone } from "../componentes/icones";
import { diaEmBrasilia, reais } from "../formato";

const CELULA = "px-2 py-1.5";
const CABECA =
  "sticky top-0 bg-painel-2 text-left text-rotulo tracking-[0.06em] text-tinta-3 uppercase";
const CAMPO =
  "min-h-8 rounded-botao border border-linha bg-painel-2 px-2 text-[16px] text-tinta md:text-[11.5px]";

export function TarifasDoContrato() {
  const [tela, definirTela] = useState<TarifasNaTela | null>(null);
  const [erro, definirErro] = useState<ErroDaApi | null>(null);

  const carregar = useCallback(async () => {
    try {
      definirTela(await api<TarifasNaTela>("/tollvision/tarifas"));
      definirErro(null);
    } catch (falha) {
      definirErro(
        falha instanceof ErroDaApi
          ? falha
          : new ErroDaApi("Falha inesperada.", 0),
      );
    }
  }, []);

  useEffect(() => {
    void carregar();
  }, [carregar]);

  if (erro)
    return (
      <Estado
        tipo={
          erro.status === 402
            ? "sem-licenca"
            : erro.status === 403
              ? "bloqueado"
              : "erro"
        }
        titulo={
          erro.status === 402
            ? "As tarifas são do módulo TollVision OCR, que esta organização não contratou."
            : erro.status === 403
              ? "As tarifas são da supervisão, do backoffice e do administrador."
              : "Não foi possível carregar as tarifas."
        }
        detalhe={erro.message}
      />
    );
  if (tela === null) return <Estado tipo="carregando" titulo="Carregando…" />;
  if (tela.concessoes.length === 0)
    return (
      <Estado
        tipo="vazio"
        titulo="Nenhuma concessão no seu recorte."
        detalhe="As tarifas são de cada concessão: quem tem recorte vê só as dele."
      />
    );
  return (
    <div className="grid gap-3">
      {tela.concessoes.map((c) => (
        <DaConcessao
          key={c.concessao_id}
          concessao={c}
          podeCadastrar={tela.pode_cadastrar}
          aoGravar={carregar}
        />
      ))}
    </div>
  );
}

function DaConcessao({
  concessao,
  podeCadastrar,
  aoGravar,
}: {
  concessao: TarifasDaConcessao;
  podeCadastrar: boolean;
  aoGravar: () => Promise<void>;
}) {
  const { estrutura } = concessao;
  return (
    <Painel
      titulo={`${concessao.concessao} · ${concessao.codigo}`}
      acoes={<Badge cor="azul">{estrutura.nome}</Badge>}
      rodape={
        estrutura.pendente ? (
          <span>
            <b className="text-tinta-2">A confirmar com a concessionária:</b>{" "}
            {estrutura.pendente}
          </span>
        ) : undefined
      }
    >
      <div className="grid gap-3">
        <p className="text-[11.5px] text-tinta-3">
          {concessao.perfil ? (
            <>
              A estrutura do contrato, pelo perfil do poder concedente{" "}
              <b className="text-tinta-2">{concessao.perfil}</b>.
            </>
          ) : (
            <>
              Sem perfil do poder concedente: vale a estrutura padrão do
              cockpit, em que o valor de cada categoria é o cadastro.
            </>
          )}{" "}
          Categorias {estrutura.faixa}.
          {estrutura.fonte && <> Fonte: {estrutura.fonte}.</>}
        </p>

        {concessao.fora_da_estrutura.length > 0 && (
          <div
            role="alert"
            className="flex flex-wrap items-center gap-2 rounded-[7px] border border-amarelo-borda bg-amarelo-fundo px-2.5 py-2 text-[11.5px] text-tinta-2"
          >
            <Badge cor="amarelo">fora do contrato</Badge>
            {concessao.fora_da_estrutura.length === 1
              ? `A categoria ${concessao.fora_da_estrutura[0]} está cadastrada, mas o contrato não a tem.`
              : `As categorias ${concessao.fora_da_estrutura.join(", ")} estão cadastradas, mas o contrato não as tem.`}{" "}
            Nenhuma passagem é aprovada nela — o cadastro é anterior ao perfil,
            ou é erro.
          </div>
        )}

        <Categorias estrutura={estrutura} />
        {estrutura.meios.length > 0 && <Meios estrutura={estrutura} />}
        {estrutura.regras.length > 0 && (
          <Lista titulo="O que o contrato manda" itens={estrutura.regras} />
        )}
        {estrutura.trechos.length > 0 && (
          <Porticos trechos={estrutura.trechos} />
        )}
        {estrutura.isencoes.length > 0 && (
          <Lista titulo="Isenções" itens={estrutura.isencoes} />
        )}
        <Cadastro cadastro={concessao.cadastro} />
        {podeCadastrar && (
          <TabelaNova concessao={concessao} aoGravar={aoGravar} />
        )}
      </div>
    </Painel>
  );
}

function Subtitulo({ children }: { children: string }) {
  return (
    <h3 className="text-titulo-cartao font-bold tracking-[0.07em] text-tinta-3 uppercase">
      {children}
    </h3>
  );
}

function numero(valor: number | null): string {
  return valor === null ? "—" : valor.toLocaleString("pt-BR");
}

function eixos(c: CategoriaDaEstrutura): string {
  if (c.tipo === "ISENTA") return "—";
  if (c.eixos === null) return "—";
  return c.ou_mais ? `${c.eixos} ou mais` : String(c.eixos);
}

function Categorias({ estrutura }: { estrutura: EstruturaNaTela }) {
  const multiplica = estrutura.por_multiplicador;
  return (
    <section aria-label="Categorias do contrato" className="grid gap-1.5">
      <Subtitulo>Categorias</Subtitulo>
      <div className="min-h-0 overflow-auto">
        <table className="w-full min-w-[560px] border-collapse text-[11.5px]">
          <thead className={CABECA}>
            <tr>
              <th className={CELULA}>Categoria</th>
              <th className={CELULA}>Veículo</th>
              <th className={`${CELULA} text-right`}>Eixos</th>
              <th className={CELULA}>Rodagem</th>
              {multiplica && (
                <>
                  <th className={`${CELULA} text-right`}>Multiplicador</th>
                  <th className={`${CELULA} text-right`}>Com TAG</th>
                </>
              )}
            </tr>
          </thead>
          <tbody>
            {estrutura.categorias.map((c) => (
              <tr key={c.numero} className="border-t border-linha-suave">
                <td className={`${CELULA} mono font-bold text-tinta`}>
                  {c.numero}
                </td>
                <td className={`${CELULA} text-tinta-2`}>
                  {c.classe}
                  {c.tipo !== "COMUM" && (
                    <>
                      {" "}
                      <Badge cor={c.tipo === "ISENTA" ? "verde" : "cinza"}>
                        {c.tipo === "ISENTA" ? "isenta" : "especial"}
                      </Badge>
                    </>
                  )}
                </td>
                <td className={`${CELULA} mono text-right text-tinta-2`}>
                  {eixos(c)}
                </td>
                <td className={`${CELULA} text-tinta-2`}>{c.rodagem ?? "—"}</td>
                {multiplica && (
                  <>
                    <td className={`${CELULA} mono text-right text-tinta-2`}>
                      {numero(c.multiplicador)}
                    </td>
                    <td className={`${CELULA} mono text-right text-tinta-2`}>
                      {numero(c.multiplicador_tag)}
                    </td>
                  </>
                )}
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      {!multiplica && (
        <p className="text-[11px] text-tinta-3">
          O valor de cada categoria é o do cadastro, abaixo.
        </p>
      )}
    </section>
  );
}

function Meios({ estrutura }: { estrutura: EstruturaNaTela }) {
  return (
    <section aria-label="Meios de pagamento" className="grid gap-1.5">
      <Subtitulo>Meios de pagamento</Subtitulo>
      <ul className="flex flex-wrap gap-1.5">
        {estrutura.meios.map((m) => (
          <li
            key={m.meio}
            className="rounded-[7px] border border-linha bg-painel-2 px-2 py-1 text-[11.5px] text-tinta-2"
          >
            {m.rotulo}:{" "}
            <b className="mono text-tinta">
              {m.desconto ? `−${numero(m.desconto)}%` : "sem desconto"}
            </b>
          </li>
        ))}
      </ul>
    </section>
  );
}

function Lista({ titulo, itens }: { titulo: string; itens: string[] }) {
  return (
    <section aria-label={titulo} className="grid gap-1.5">
      <Subtitulo>{titulo}</Subtitulo>
      <ul className="grid list-disc gap-1 pl-5 text-[11.5px] text-tinta-2">
        {itens.map((i) => (
          <li key={i}>{i}</li>
        ))}
      </ul>
    </section>
  );
}

function emReais(valor: number | null): string {
  return valor === null ? "—" : reais(Math.round(valor * 100));
}

function Porticos({ trechos }: { trechos: TrechoNaTela[] }) {
  return (
    <section aria-label="Pórticos do contrato" className="grid gap-1.5">
      <Subtitulo>Pórticos e a categoria 1 na data-base</Subtitulo>
      <div className="min-h-0 overflow-auto">
        <table className="w-full min-w-[640px] border-collapse text-[11.5px]">
          <thead className={CABECA}>
            <tr>
              <th className={CELULA}>Pórtico</th>
              <th className={CELULA}>Rodovia e km</th>
              <th className={CELULA}>Município</th>
              <th className={`${CELULA} text-right`}>Trecho (km)</th>
              <th className={`${CELULA} text-right`}>Contrato</th>
              <th className={`${CELULA} text-right`}>A conta dá</th>
            </tr>
          </thead>
          <tbody>
            {trechos.map((t) => {
              const confere = t.tarifa_calculada === t.tarifa_do_contrato;
              return (
                <tr key={t.portico} className="border-t border-linha-suave">
                  <td className={`${CELULA} mono font-bold text-tinta`}>
                    {t.portico}
                  </td>
                  <td className={`${CELULA} text-tinta-2`}>
                    {t.rodovia}, km {t.km}
                  </td>
                  <td className={`${CELULA} text-tinta-2`}>{t.municipio}</td>
                  <td className={`${CELULA} mono text-right text-tinta-2`}>
                    {numero(t.tcp_simples)}
                  </td>
                  <td className={`${CELULA} mono text-right text-tinta-2`}>
                    {emReais(t.tarifa_do_contrato)}
                  </td>
                  <td className={`${CELULA} mono text-right text-tinta`}>
                    {emReais(t.tarifa_calculada)}{" "}
                    <Badge cor={confere ? "verde" : "amarelo"}>
                      {confere ? "confere" : "diverge"}
                    </Badge>
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
    </section>
  );
}

function Cadastro({ cadastro }: { cadastro: TarifaCadastrada[] }) {
  const comTag = cadastro.some((t) => t.valor_tag_centavos !== null);
  return (
    <section aria-label="Tarifas cadastradas" className="grid gap-1.5">
      <Subtitulo>Cadastro e histórico</Subtitulo>
      {cadastro.length === 0 ? (
        <p className="text-[11.5px] text-tinta-3">
          Nenhuma tarifa cadastrada: a passagem aprovada fica "sem tarifa" até o
          cadastro.
        </p>
      ) : (
        <div className="min-h-0 overflow-auto">
          <table className="w-full min-w-[640px] border-collapse text-[11.5px]">
            <thead className={CABECA}>
              <tr>
                <th className={CELULA}>Categoria</th>
                <th className={CELULA}>Praça</th>
                <th className={`${CELULA} text-right`}>Valor</th>
                {comTag && <th className={`${CELULA} text-right`}>Com TAG</th>}
                <th className={CELULA}>Vigência</th>
                <th className={CELULA}>Fonte</th>
              </tr>
            </thead>
            <tbody>
              {cadastro.map((t) => (
                <tr
                  key={`${t.categoria}-${t.praca ?? ""}-${t.vigente_desde}`}
                  className="border-t border-linha-suave"
                >
                  <td className={`${CELULA} mono font-bold text-tinta`}>
                    {t.categoria}
                  </td>
                  <td className={`${CELULA} text-tinta-2`}>
                    {t.praca
                      ? t.praca_codigo
                        ? `${t.praca_codigo} · ${t.praca}`
                        : t.praca
                      : "Todas"}
                  </td>
                  <td className={`${CELULA} mono text-right text-tinta`}>
                    {reais(t.valor_centavos)}
                  </td>
                  {comTag && (
                    <td className={`${CELULA} mono text-right text-tinta-2`}>
                      {t.valor_tag_centavos === null
                        ? "—"
                        : reais(t.valor_tag_centavos)}
                    </td>
                  )}
                  <td className={`${CELULA} text-tinta-2`}>
                    desde {diaEmBrasilia(t.vigente_desde)}
                    {t.vigente_ate &&
                      ` até ${diaEmBrasilia(t.vigente_ate)}`}{" "}
                    {t.vigente && <Badge cor="verde">vigente</Badge>}
                  </td>
                  <td className={`${CELULA} text-tinta-3`}>{t.fonte}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </section>
  );
}

/** O texto do arquivo. `File.text()` é o caminho; o `FileReader` fica para quem não o tem. */
function lerTexto(arquivo: File): Promise<string> {
  if (typeof arquivo.text === "function") return arquivo.text();
  return new Promise((resolver, recusar) => {
    const leitor = new FileReader();
    leitor.onload = () => resolver(String(leitor.result ?? ""));
    leitor.onerror = () => recusar(leitor.error ?? new Error("leitura"));
    leitor.readAsText(arquivo);
  });
}

function baixar(conteudo: string, nome: string) {
  // Sem `URL.createObjectURL` (o teste, um navegador muito antigo) não há o que baixar.
  if (typeof URL.createObjectURL !== "function") return;
  const url = URL.createObjectURL(
    new Blob([`\uFEFF${conteudo}`], { type: "text/csv;charset=utf-8" }),
  );
  const link = document.createElement("a");
  link.href = url;
  link.download = nome;
  link.click();
  URL.revokeObjectURL(url);
}

function onde(a: ApontamentoNaTela): string {
  const partes = [
    a.linha !== null ? `linha ${a.linha}` : null,
    a.portico,
    a.categoria !== null ? `categoria ${a.categoria}` : null,
  ].filter(Boolean);
  return partes.length ? `${partes.join(" · ")}: ` : "";
}

function dia(iso: string): string {
  const [ano, mes, d] = iso.split("-");
  return `${d}/${mes}/${ano}`;
}

/** A tabela nova da concessão: o modelo, o arquivo, o dia e a fonte, a prévia e o gravar. */
function TabelaNova({
  concessao,
  aoGravar,
}: {
  concessao: TarifasDaConcessao;
  aoGravar: () => Promise<void>;
}) {
  const [arquivo, definirArquivo] = useState<{
    nome: string;
    conteudo: string;
  } | null>(null);
  const [desde, definirDesde] = useState(concessao.inicio_minimo);
  const [fonte, definirFonte] = useState("");
  const [resultado, definirResultado] = useState<ResultadoDaTabela | null>(
    null,
  );
  const [recusa, definirRecusa] = useState<string | null>(null);
  const [enviando, definirEnviando] = useState(false);

  async function enviar(aplicar: boolean, escolhido = arquivo) {
    if (!escolhido) return;
    definirEnviando(true);
    definirRecusa(null);
    try {
      const r = await api<ResultadoDaTabela>(
        `/tollvision/tarifas/${concessao.concessao_id}/tabela`,
        {
          method: "POST",
          corpo: {
            conteudo: escolhido.conteudo,
            nome_do_arquivo: escolhido.nome,
            vigente_desde: desde,
            fonte,
            aplicar,
          },
        },
      );
      definirResultado(r);
      if (aplicar) await aoGravar();
    } catch (falha) {
      definirResultado(null);
      definirRecusa(
        falha instanceof ErroDaApi
          ? falha.message
          : "Não foi possível conferir a tabela.",
      );
    } finally {
      definirEnviando(false);
    }
  }

  /** A prévia é do dia e da fonte que foram conferidos: mudou um deles, ela sai, e o gravar
   * espera a conferência de novo — senão gravaria o que a prévia não mostrou. */
  function mudar(definir: (valor: string) => void, valor: string) {
    definir(valor);
    definirResultado(null);
  }

  async function escolher(lista: FileList | null) {
    const escolhido = lista?.[0];
    if (!escolhido) return;
    const lido = { nome: escolhido.name, conteudo: await lerTexto(escolhido) };
    definirArquivo(lido);
    await enviar(false, lido);
  }

  return (
    <section aria-label="Tabela nova" className="grid gap-1.5">
      <div className="flex flex-wrap items-center gap-2">
        <Subtitulo>Tabela nova</Subtitulo>
        <Botao
          onClick={() =>
            baixar(concessao.modelo, `tarifas-${concessao.codigo}.csv`)
          }
          title="Uma linha por pórtico e categoria, com a tabela vigente ou a conta do contrato."
        >
          <Icone.Baixar />
          Modelo
        </Botao>
      </div>
      <p className="text-[11px] text-tinta-3">
        CSV com as colunas <span className="mono">portico</span>,{" "}
        <span className="mono">categoria</span> e{" "}
        <span className="mono">valor</span>
        {concessao.com_tag && (
          <>
            {" "}
            e <span className="mono">valor_tag</span>
          </>
        )}
        , em reais. A tabela entra inteira ou não entra: começa à meia-noite do
        dia escolhido, no {concessao.horario}, e encerra nesse instante a
        vigente. Nunca retroage — a passagem que já aconteceu fica com a tarifa
        que valia nela. Primeiro a prévia; nada é gravado até você confirmar.
      </p>
      <div className="flex flex-wrap items-end gap-2">
        <label className="grid gap-1 text-[11px] text-tinta-3">
          Vale desde
          <input
            type="date"
            value={desde}
            min={concessao.inicio_minimo}
            onChange={(e) => mudar(definirDesde, e.target.value)}
            className={CAMPO}
          />
        </label>
        <label className="grid min-w-[16rem] flex-1 gap-1 text-[11px] text-tinta-3">
          Fonte
          <input
            value={fonte}
            onChange={(e) => mudar(definirFonte, e.target.value)}
            placeholder="A deliberação que homologou a tabela"
            className={CAMPO}
          />
        </label>
        <input
          type="file"
          accept=".csv,text/csv"
          aria-label="Planilha de tarifas"
          onChange={(e) => void escolher(e.target.files)}
          className="text-[11.5px] text-tinta-2"
        />
        {arquivo && (
          <Botao disabled={enviando} onClick={() => void enviar(false)}>
            Conferir de novo
          </Botao>
        )}
      </div>
      {recusa && (
        <p role="alert" className="text-[11.5px] text-vermelho">
          {recusa}
        </p>
      )}
      {resultado && (
        <section aria-label="Prévia da tabela" className="grid gap-1.5">
          <div className="flex flex-wrap gap-1.5">
            <Badge cor="azul">{`${resultado.linhas} linhas`}</Badge>
            {resultado.aplicada ? (
              <Badge cor="verde">{`${resultado.gravadas} gravadas`}</Badge>
            ) : resultado.entra ? (
              <Badge cor="verde">entra</Badge>
            ) : (
              <Badge cor="vermelho">
                {`${resultado.problemas.length} ${resultado.problemas.length === 1 ? "problema" : "problemas"}`}
              </Badge>
            )}
            {resultado.avisos.length > 0 && (
              <Badge cor="amarelo">
                {`${resultado.avisos.length} ${resultado.avisos.length === 1 ? "aviso" : "avisos"}`}
              </Badge>
            )}
            <Badge cor="cinza">
              {`desde ${dia(desde)} · encerra ${resultado.encerra} vigentes`}
            </Badge>
          </div>
          {resultado.problemas.length > 0 && (
            <ul
              aria-label="Problemas"
              className="grid max-h-40 gap-0.5 overflow-auto text-[11px] text-vermelho"
            >
              {resultado.problemas.slice(0, 50).map((a, i) => (
                <li key={`p-${i}`}>
                  <span className="mono text-tinta-3">{onde(a)}</span>
                  {a.motivo}
                </li>
              ))}
            </ul>
          )}
          {resultado.avisos.length > 0 && (
            <ul
              aria-label="Avisos"
              className="grid max-h-40 gap-0.5 overflow-auto text-[11px] text-tinta-2"
            >
              {resultado.avisos.slice(0, 50).map((a, i) => (
                <li key={`a-${i}`}>
                  <span className="mono text-tinta-3">{onde(a)}</span>
                  {a.motivo}
                </li>
              ))}
            </ul>
          )}
          {!resultado.aplicada && resultado.entra && (
            <Botao
              papel="aprovar"
              disabled={enviando}
              onClick={() => void enviar(true)}
            >
              {enviando
                ? "Gravando…"
                : `Gravar a tabela (${resultado.linhas} linhas)`}
            </Botao>
          )}
        </section>
      )}
    </section>
  );
}
